import { NetworkStatus } from '@apollo/client';
import isEqual from 'hs-lodash/isEqual';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { QUERY_CLIENTS } from '../constants/query-clients';
const queryDataIdentity = queryResult => queryResult;
export const createUseSelectQueryData = Context => {
  function useSelectQueryData({
    fieldName,
    selector: maybeSelector,
    variables
  }) {
    const {
      getCachedQueryState,
      subscribe,
      getRefetch
    } = useContext(Context);
    const selector = maybeSelector !== null && maybeSelector !== void 0 ? maybeSelector : queryDataIdentity;
    const refetch = useMemo(() => getRefetch({
      clientType: QUERY_CLIENTS.DFC,
      fieldName,
      variables
    }), [getRefetch, fieldName, variables]);

    // This type is defined dynamically because of the default behavior to use
    // an identity function. Without it, TypeScript gets confused about the type of `queryData`
    const [selectedQueryData, setSelectedQueryData] = useState(() => {
      const queryState = getCachedQueryState({
        clientType: QUERY_CLIENTS.DFC,
        fieldName,
        variables
      });
      let initialParams = {
        // Set initial loading state to true, a fetch will be kicked off when the subscription
        // is created
        loading: true,
        networkStatus: NetworkStatus.loading,
        refetch
      };
      if (queryState) {
        initialParams = Object.assign({}, queryState, {
          refetch
        });
      }
      return selector(initialParams);
    });
    const lastResult = useRef();
    useEffect(() => {
      let subscription = subscribe({
        clientType: QUERY_CLIENTS.DFC,
        fieldName,
        observer: queryResult => {
          lastResult.current = Object.assign({}, queryResult, {
            refetch
          });
          setSelectedQueryData(prev => {
            // this non-null assertion is safe since we set `lastResult.current` right above this
            const next = selector(lastResult.current);
            if (!isEqual(next, prev)) {
              return next;
            }
            return prev;
          });
        },
        onError: error => {
          lastResult.current = Object.assign({}, lastResult.current, {
            error,
            refetch
          });
          setSelectedQueryData(prev => {
            // this non-null assertion is safe since we set `lastResult.current` right above this
            const next = selector(lastResult.current);
            if (!isEqual(next, prev)) {
              return next;
            }
            return prev;
          });
        },
        /**
         * We need to leverage `onResubscribe` to ensure that the subscription is properly released
         * when this hook instance unloads.
         *
         * Whenever the query gets an error, all observers get resubscribed when that error is
         * cleared out. If we don't update `subscription` here, when this hook unloads, it'll be
         * unsubscribing to an old subscription and could leave the active subscription alive.
         */
        onResubscribe: newSubscription => {
          subscription = newSubscription;
        },
        variables
      });
      return () => {
        subscription.unsubscribe();
      };
    }, [fieldName, refetch, selector, subscribe, variables]);
    return selectedQueryData;
  }
  return useSelectQueryData;
};